import { postToDevice, type CameraScanStatus } from './deviceBridge'

/**
 * The tablet app's camera scanner, asked by a screen (Tablet Browser 2.15, `camera-scan`;
 * docs/tablets.md). A request and ONE answer, matched by its requestId, so it never goes on the
 * scan bus: the screen that asked is the screen that is answered, and decides what the value
 * means exactly as it would a gun scan.
 *
 * `feedback: 'off'` by default: the app's own beep would come as the value is read, before the
 * site has judged it, and on this site a beep means yes (utils/scanSource/gunFeedback.ts). The
 * screen sounds the gun with its verdict, as for any scan.
 *
 * Asked only where `capabilities` listed the mode (utils/scanSource/tabletInfo.ts): an app
 * older than 2.15 never answers. A request still unanswered after `ANSWER_LIMIT_MS` counts as
 * cancelled, so a screen is never left waiting on an app that went away.
 */

export type CameraMode = 'barcode' | 'text' | 'container' | 'auto'

export type CameraAnswer = {
  status: CameraScanStatus
  value: string
  /** For a container: whether its check digit was right (the app answers only proven numbers). */
  checkDigitValid: boolean | null
  errorMessage: string
}

/** The longest a driver might hold the camera on a worn number before giving up. */
export const ANSWER_LIMIT_MS = 120_000

const waiting = new Map<string, (answer: CameraAnswer) => void>()
let asked = 0

export function requestCameraScan(mode: CameraMode, options: { prompt?: string, feedback?: 'on' | 'off' } = {}): Promise<CameraAnswer> {
  asked += 1
  const requestId = `page-${Date.now().toString(36)}-${asked}`
  return new Promise((resolve) => {
    const timer = setTimeout(() => finish({ status: 'cancelled', value: '', checkDigitValid: null, errorMessage: 'The camera did not answer.' }), ANSWER_LIMIT_MS)
    function finish(answer: CameraAnswer) {
      clearTimeout(timer)
      waiting.delete(requestId)
      resolve(answer)
    }
    waiting.set(requestId, finish)
    const sent = postToDevice({
      command: 'camera-scan',
      requestId,
      mode,
      feedback: options.feedback ?? 'off',
      ...(options.prompt ? { prompt: options.prompt } : {}),
    })
    if (!sent) finish({ status: 'error', value: '', checkDigitValid: null, errorMessage: 'There is no tablet app to ask.' })
  })
}

/** The app answered: hand it to the screen that asked. An answer nobody is waiting for is dropped. */
export function settleCameraScan(answer: { requestId: string } & CameraAnswer): void {
  waiting.get(answer.requestId)?.({
    status: answer.status,
    value: answer.value,
    checkDigitValid: answer.checkDigitValid,
    errorMessage: answer.errorMessage,
  })
}
